import React, { Component } from "react";
import { FormattedMessage, injectIntl, type IntlShape } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";
import { NavLink, Redirect, type RouteComponentProps } from "react-router-dom";

import { tryToApprovePlan } from "~/actions/approve_plan";
import { getPatientId } from "~/actions/get_patient_id";
import { Status } from "~/common/courses";
import { remoteLog } from "~/common/logging";
import { isPatient } from "~/common/patient";
import { isUser } from "~/common/user";
import { formatPersonName } from "~/components/common/PersonName";
import { Button } from "~/components/ui/button";
import { Layout } from "~/components/ui/layout";
import { Loader } from "~/components/ui/loader";
import { LoadingButton } from "~/components/ui/loading-button";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { setDocumentTitle } from "~/hooks/use-document-title";
import type { RootState } from "~/store";

const mapStateToProps = (state: RootState) => {
  return {
    user: state.user,
    patient: state.patient,
  };
};

const mapDispatchToProps = {
  getPatient: getPatientId,
  planApprove: tryToApprovePlan,
};

type PlanApproveProps = PropsFromRedux & { intl: IntlShape } & RouteComponentProps<{
    patient_id: string;
    case_id: string;
  }>;

type PlanApproveState = {
  case_id: string | null;
  patient_id: string | null;
  isDisclamerAccepted: boolean;
  isSubmitting: boolean;
  redirect_to_rework: boolean;
  steps: number | string;
};

class PlanApprove extends Component<PlanApproveProps, PlanApproveState> {
  constructor(props: PlanApproveProps) {
    super(props);
    this.state = {
      case_id: null,
      patient_id: null,
      isDisclamerAccepted: false,
      isSubmitting: false,
      redirect_to_rework: true,
      steps: 0,
    };
    this.approvePlan = this.approvePlan.bind(this);
    this.acceptDisclamer = this.acceptDisclamer.bind(this);
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "3d_plan_approve_page");
  }

  componentDidMount() {
    const { case_id, patient_id } = this.props.match.params;
    const steps = this.props.location.search.replace("?steps=", "");

    this.setState({ case_id, patient_id, steps });
    void this.props.getPatient(Number(patient_id));

    setDocumentTitle(this.props.intl.formatMessage({ id: "plan.approve.header" }), patient_id);
  }

  acceptDisclamer(data: HTMLInputElement) {
    this.setState({ isDisclamerAccepted: data.checked });
  }

  async approvePlan() {
    try {
      this.setState({ isSubmitting: true, isDisclamerAccepted: false, redirect_to_rework: false });
      await this.props.planApprove(this.state.patient_id, this.state.case_id, this.state.steps);
    } finally {
      this.props.history.push("/pages/patients");
    }
  }

  render() {
    const { user, patient, intl } = this.props;

    if (!isUser(user) || !isPatient(patient)) {
      return (
        <Layout size="md-8">
          <Loader />
        </Layout>
      );
    }

    if (patient.status != Status._3D_PLAN_APPROVAL) {
      return <Redirect to="/pages/patients" />;
    }

    const userHasName = user.first_name.trim().length > 0 || user.last_name.trim().length > 0;

    if (!userHasName) {
      return <Redirect to="/pages/patients" />;
    }

    const clinicTitle = patient.clinic.title;

    if (!clinicTitle) {
      return (
        <Layout size="md-8">
          <Portlet as="main">
            <PortletTitle iconClassName="icon-book-open">
              <FormattedMessage id="plan.approve.header" />
            </PortletTitle>

            <div className="portlet-body">
              <FormattedMessage id="plan.approve.no_clinic" />

              <NavLink to={`/pages/patient/${this.state.patient_id}`}>
                <FormattedMessage id="plan.approve.redirect" />
              </NavLink>
            </div>
          </Portlet>
        </Layout>
      );
    }

    const DOCTOR = formatPersonName({ person: user, useMiddleName: true, isDoctor: true, user });

    const organization = "Limited liability company Dommar";
    const company = "Dommar";
    const system = "3D Smile System";

    const DISCLAMER = {
      ru: `Я, ${DOCTOR} (далее также «Врач»), занимающий(-ая) должность врач-стоматолог в клинике "${clinicTitle}" настоящим выражаю свое согласие и подтверждаю следующие положения: \n 1. Я подтверждаю, что внимательно ознакомился с 3D планом, изготовленным Обществом с ограниченной ответственностью «Доммар» (ОГРН: 1147746363368) (далее – «Доммар»), а именно с представленной на 3D плане визуализацией движения зубов на каждой челюсти пациента по каждому этапу лечения. Доммар является разработчиком и производителем системы выравнивания зубов и ортодонтических кап 3D Smile (далее – «Капы»).\n 2. На основании специальных знаний в области ортодонтии и имеющегося у меня опыта ортодонтонтического лечения, настоящим я подтверждаю, что представленная на 3D плане визуализация движения зубов на каждой челюсти пациента по каждому этапу лечения точно, правильно и с полнотой соответствует моему рецепту и моим комментариям в личном кабинете. Настоящим я соглашаюсь с представленной на 3D плане визуализацией движения зубов, одобряю 3D план и подтверждаю, что 3D план составлен в полном соответствии с предоставленными мною в личном кабинете Врача инструкциям и указаниям, а также заявляю, что у меня нет возражений, замечаний и (или) иных комментариев к 3D плану.\n 3. Я понимаю, что моделирование 3D плана осуществляет зубной техник на основании предоставленной мною информации, и подтверждаю клиническую реализуемость 3D плана на основании моих знаний и опыта в области ортодонтии. Вместе с тем, я понимаю, что для клинической реализуемости 3D плана необходимо соблюдение пациентом режима ношения кап, изготовленных согласно 3D плану, проведение специальных манипуляций (сепарация, установка замков) и регулярный клинический контроль.\n 4. Я понимаю, что информация, предоставленная мною в личном кабинете, может содержать персональные данные пациента. \n 5. Я подтверждаю, что при запросе на создание ссылки на 3D план для пациента я предоставляю Доммар e-mail или телефон пациента, предварительно подписав с ним согласие на обработку его/её персональных данных, в том числе, путем передачи таких данных Доммар для целей создания 3D плана его/её зубов.\n 6. Утверждая данный 3D план, даю свое согласие на изготовление Кап согласно данному 3D плану, принимаю условия оплаты и выражаю готовность произвести оплату.\n`,
      en: `I, ${DOCTOR} (hereinafter “Doctor”), holding the position Dentist at "${clinicTitle}", hereby announce my consent and confirm the following provisions: \n 1. I confirm that I had studied the 3D plan, prepared by the ${organization} (hereinafter “${company}”), carefully, in particular with regards to visualizations of the teeth movement at both of the patient’s arches for each stage of the treatment. ${company} is the developer and producer of the ${system} of teeth alignment and orthodontic aligners (hereinafter “Aligners”). \n 2. Based on specialized orthodontic knowledge and my experience in orthodontic treatments, I hereby confirm that the visualization of teeth movement at both of the patient’s arches for each stage of the treatment, presented in the 3D plan, accurately, correctly and fully corresponds to my instructions and comments that were left in my Doctor Account. I hereby agree with the visualization of teeth movement, presented in the 3D plan, approve the 3D plan, and confirm that the 3D plan was compiled in full accordance with my instructions and directions that were left in my Doctor Account. I also confirm that I have no objections and/or other comments regarding the 3D plan. \n 3.  I understand that the 3D plan is modeled by the dental technician on the basis of the data that I supplied, and I confirm the clinical feasibility of the 3D plan on the basis of my knowledge and orthodontic experience. At the same time, I understand that the 3D plan’s clinical feasibility depends on the patient’s adherence to the regime of wearing aligners, manufactured in accordance with the 3D plan, execution of special operations (IPR, installation of attachments), and regular in-clinic control. \n 4. I understand that the data provided by me in the Doctor Account may contain the patient’s personal information. \n 5.  I confirm that in requesting a shareable link to the 3D plan for my patient, I provide Dommar with the patient’s email or phone number, and that the patient had preliminarily signed the consent for personal data processing, including the transfer of such data to Dommar for the creation of the 3D plan of patient’s teeth. \n 6. By confirming this 3D plan, I consent to the manufacturing of Aligners in accordance with the 3D plan, accept the payment terms, and indicate my readiness to make the payment. \n`,
      en_predict: `I, ${DOCTOR} (hereinafter “Doctor”), holding the position Dentist at "${clinicTitle}", hereby announce my consent and confirm the following provisions: \n 1. I confirm that I had studied the 3D plan, prepared by the ${organization}, carefully, in particular with regards to visualizations of the teeth movement at both of the patient’s arches for each stage of the treatment. ${organization} is the developer and producer of the ${system} of teeth alignment and orthodontic aligners (hereinafter “Aligners”). \n 2. Based on specialized orthodontic knowledge and my experience in orthodontic treatments, I hereby confirm that the visualization of teeth movement at both of the patient’s arches for each stage of the treatment, presented in the 3D plan, accurately, correctly and fully corresponds to my instructions and comments that were left in my Doctor Account. I hereby agree with the visualization of teeth movement, presented in the 3D plan, approve the 3D plan, and confirm that the 3D plan was compiled in full accordance with my instructions and directions that were left in my Doctor Account. I also confirm that I have no objections and/or other comments regarding the 3D plan. \n 3.  I understand that the 3D plan is modeled by the dental technician on the basis of the data that I supplied, and I confirm the clinical feasibility of the 3D plan on the basis of my knowledge and orthodontic experience. At the same time, I understand that the 3D plan’s clinical feasibility depends on the patient’s adherence to the regime of wearing aligners, manufactured in accordance with the 3D plan, execution of special operations (IPR, installation of attachments), and regular in-clinic control. \n 4. I understand that the data provided by me in the Doctor Account may contain the patient’s personal information. \n 5.  I confirm that in requesting a shareable link to the 3D plan for my patient, I provide ${organization} with the patient’s email or phone number, and that the patient had preliminarily signed the consent for personal data processing, including the transfer of such data to ${organization} for the creation of the 3D plan of patient’s teeth. \n 6. By confirming this 3D plan, I consent to the manufacturing of Aligners in accordance with the 3D plan, accept the payment terms, and indicate my readiness to make the payment. \n`,
    };

    const NEET_TO_READ = {
      ru: `Внимательно прочтите условия настоящего документа перед утверждением 3D-плана движения зубов пациента, сопровождаемого настоящим документом («3D-план»).
            Посредством нажатия на кнопку «Утвердить», Вы признаете тот факт, что ознакомились с настоящим документом, Вам понятны его условия, Вы принимаете их и соглашаетесь быть связанными ими. В том случае, если Вы не имеете намерения связывать себя условиями настоящего документа, Вам следует нажать на кнопку «Отложить утверждение».`,
      en: `Please, read the terms of this document carefully before confirming the 3D plan of patient’s teeth movement, which is accompanied by this document (“3D plan”).
            By clicking the “Confirm” button, you acknowledge that you have read this document, understand and accept its terms, and agree to be bound by it. If you have no plans to bind yourself by the terms of this document, click the “Postpone confirmation” button.`,
    };

    return (
      <Layout size="md-8">
        <Portlet as="main">
          <PortletTitle iconClassName="icon-book-open">
            <FormattedMessage id="plan.approve.header" />
          </PortletTitle>

          <div className="portlet-body">
            <div style={{ fontStyle: "italic" }}>{NEET_TO_READ[intl.locale]}</div>

            <div className="tw-my-4">
              <FormattedMessage id="plan.approve.body" /> {this.state.case_id}
            </div>

            <div className="form-group">
              <textarea
                className="form-control"
                rows={16}
                name="comment"
                defaultValue={DISCLAMER[intl.locale]}
                readOnly={true}
              />
            </div>

            <div className="form-group">
              <label
                id="approve-disclaimer-accept-label"
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <input
                  id="approve-disclaimer-accept-value"
                  style={{ margin: 0 }}
                  type="checkbox"
                  onClick={(e) => this.acceptDisclamer(e.currentTarget)}
                  disabled={this.state.isSubmitting}
                />
                <FormattedMessage id="plan.approve.accept" />
              </label>
            </div>

            <div className="tw-flex tw-flex-wrap tw-gap-3 sm:tw-gap-2">
              <LoadingButton
                onClick={() => this.approvePlan()}
                disabled={!this.state.isDisclamerAccepted}
                isLoading={this.state.isSubmitting}
              >
                <FormattedMessage id="plan.confirm_plan.btn" />
              </LoadingButton>

              <NavLink to={`/pages/patient/${this.state.patient_id}/rework/${this.state.case_id}`}>
                <Button disabled={!this.state.redirect_to_rework}>
                  <FormattedMessage id="plan.approve.postpone" />
                </Button>
              </NavLink>
            </div>
          </div>
        </Portlet>
      </Layout>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(PlanApprove));
